/**
 * The chat turns this panel has running, each kept on its own — so a turn
 * keeps going in the background when the user starts a new chat or switches
 * to another conversation, and switching back shows it live again
 * (MEIS-13516). One registry per `useAgentChat` (i.e. per panel and
 * identity), outliving every `AgentChatBox` remount; `clear()` on an
 * identity switch drops everything.
 *
 * `useAgentChat.send` still reads each turn's stream; it records every event
 * here and only feeds the reducer (and the sending box's stream) while the
 * turn is `attached` — shown in the panel. A turn is:
 * - `foreground` while the box that sent it still consumes its stream; lost
 *   for good on the first detach (that box's stream is aborted by the new
 *   chat or the switch),
 * - `attached` while its conversation is the one shown — again after a
 *   switch back (`attach`), then fed to the box from `events` instead.
 *
 * A settled turn leaves the registry: the stored conversation has its
 * answer from then on. One that finished while detached is reported
 * (`onFinished`) and stays
 * - "unseen" until its conversation is opened or the history is shown — the
 *   panel's finished notice and history badge,
 * - "unopened" until its conversation is opened — the history entry's green
 *   check, which the history itself is there to show.
 *
 * Listeners are held per subscription, not per function: a subscription
 * cleaned up late (after the same listener already subscribed again, e.g.
 * React's StrictMode remount) ends only itself, never the live one.
 */
import type { AgentChatResult, AgentStreamEvent } from "./agentStream.js";
import type { AgentChatMessage } from "./useAgentChat.js";

export type AgentBackgroundTurnStatus = "running" | "finished" | "failed";

export type AgentBackgroundTurn = {
  readonly key: string;
  /** Known once the send mutation answered (`nameConversation`) — a new chat has none before. */
  conversationId: string | null;
  /** The conversation as it stood when the turn was sent, its user message last. */
  readonly messages: readonly AgentChatMessage[];
  /**
   * The id the sending box streams the answer under (the chat stream's
   * "start" chunk), so a box reopening the turn shows it under the same id
   * (`runningAnswerMessage`) — null when the sender gave none.
   */
  readonly messageId: string | null;
  /** Every non-terminal event so far (pings left out), in order. */
  readonly events: AgentStreamEvent[];
  /** A title for the history until the server lists the conversation (`provisionalConversationTitle`). */
  readonly title: string;
  status: AgentBackgroundTurnStatus;
  /** The finished answer, set by `settle` — read by listeners of that last update. */
  result: AgentChatResult | null;
  attached: boolean;
  foreground: boolean;
  /** The user asked to stop this turn (`stopAgentTurn`); it still ends through its result. */
  stopRequested: boolean;
  /** Aborted by `clear()`: the turn's stream reader stops. */
  readonly signal: AbortSignal;
};

export type AgentFinishedTurnNotice = {
  conversationId: string;
  title: string;
};

type Entry = AgentBackgroundTurn & { abort: () => void };

const MAX_PROVISIONAL_TITLE = 60;

/**
 * A new conversation's first message as a one-line title, shown in the
 * history until the server's own list carries the conversation.
 */
export function provisionalConversationTitle(firstMessage: string): string {
  const text = firstMessage.replace(/\s+/g, " ").trim();
  return text.length > MAX_PROVISIONAL_TITLE
    ? `${text.slice(0, MAX_PROVISIONAL_TITLE - 1)}…`
    : text;
}

let nextKey = 0;

export class AgentBackgroundTurns {
  private turns = new Map<string, Entry>();
  private unseenFinished = new Set<string>();
  private unopenedFinished = new Set<string>();
  /** Conversations whose turn this client saw end — a stale server `running` flag is not trusted for them. */
  private finishedLocally = new Set<string>();
  private listeners = new Map<
    symbol,
    (turn: AgentBackgroundTurn | null) => void
  >();
  private finishedListeners = new Map<
    symbol,
    (notice: AgentFinishedTurnNotice) => void
  >();
  private version = 0;

  /** A turn the shown chat just sent: attached and in the foreground; every other turn is detached. */
  begin(params: {
    messages: readonly AgentChatMessage[];
    conversationId: string | null;
    title: string;
    messageId?: string;
  }): AgentBackgroundTurn {
    for (const turn of this.turns.values()) {
      turn.attached = false;
      turn.foreground = false;
    }
    nextKey += 1;
    const controller = new AbortController();
    const entry: Entry = {
      key: `turn-${nextKey}`,
      conversationId: params.conversationId,
      messages: params.messages,
      messageId: params.messageId ?? null,
      events: [],
      title: params.title,
      status: "running",
      result: null,
      attached: true,
      foreground: true,
      stopRequested: false,
      signal: controller.signal,
      abort: () => controller.abort(),
    };
    this.turns.set(entry.key, entry);
    if (entry.conversationId) this.finishedLocally.delete(entry.conversationId);
    this.notify(entry);
    return entry;
  }

  /**
   * Names a running turn's conversation — as soon as the send mutation
   * answers (it carries the id, also for a brand-new conversation the
   * backend just created), so the chat is listed right away instead of one
   * more round trip later, when its stream's `conversation` event arrives.
   * Returns whether this named it (`false` when it already had this id, or
   * the turn is no longer running).
   */
  nameConversation(key: string, conversationId: string): boolean {
    const turn = this.turns.get(key);
    if (!turn || turn.status !== "running") return false;
    if (turn.conversationId === conversationId) return false;
    turn.conversationId = conversationId;
    this.finishedLocally.delete(conversationId);
    this.notify(turn);
    return true;
  }

  /** Records one stream event of a running turn. */
  record(key: string, event: AgentStreamEvent): void {
    const turn = this.turns.get(key);
    if (!turn || turn.status !== "running") return;
    if (event.type === "ping" || event.type === "result") return;
    if (event.type === "error") return;
    if (event.type === "conversation") {
      turn.conversationId = event.conversationId;
      this.finishedLocally.delete(event.conversationId);
    }
    turn.events.push(event);
    this.notify(turn);
  }

  /**
   * Ends a turn: reported (and remembered as unseen) when it finished while
   * detached, handed to the listeners one last time (with its `result`),
   * then forgotten.
   */
  settle(
    key: string,
    outcome:
      | { status: "finished"; result: AgentChatResult }
      | { status: "failed" }
  ): void {
    const turn = this.turns.get(key);
    if (!turn) return;
    turn.status = outcome.status;
    turn.result = outcome.status === "finished" ? outcome.result : null;
    this.turns.delete(key);
    const { conversationId } = turn;
    if (conversationId) this.finishedLocally.add(conversationId);
    if (outcome.status === "finished" && !turn.attached && conversationId) {
      this.unseenFinished.add(conversationId);
      this.unopenedFinished.add(conversationId);
      this.finishedListeners.forEach(listener =>
        listener({ conversationId, title: turn.title })
      );
    }
    this.notify(turn);
  }

  /** Forgets a turn the backend refused (`AGENT_CONFLICT`) — nothing ran, nothing to report. */
  discard(key: string): void {
    if (!this.turns.delete(key)) return;
    this.notify(null);
  }

  /** The shown chat moved on (a new chat, another conversation): every turn keeps running unshown. */
  detachAll(): void {
    let changed = false;
    for (const turn of this.turns.values()) {
      if (!turn.attached && !turn.foreground) continue;
      turn.attached = false;
      turn.foreground = false;
      changed = true;
    }
    if (changed) this.notify(null);
  }

  /** Shows the running turn of `conversationId` again, if there is one. */
  attach(conversationId: string): AgentBackgroundTurn | null {
    const turn = this.runningEntry(conversationId);
    if (!turn) return null;
    turn.attached = true;
    this.notify(turn);
    return turn;
  }

  get(key: string): AgentBackgroundTurn | undefined {
    return this.turns.get(key);
  }

  /** Whether `key` is still a running turn (not settled, discarded or cleared). */
  isActive(key: string): boolean {
    return this.turns.has(key);
  }

  /** The turn shown right now, if one runs. */
  attached(): AgentBackgroundTurn | null {
    for (const turn of this.turns.values()) if (turn.attached) return turn;
    return null;
  }

  isRunning(conversationId: string): boolean {
    return this.runningEntry(conversationId) !== null;
  }

  runningConversationIds(): string[] {
    return Array.from(this.turns.values()).flatMap(turn =>
      turn.conversationId ? [turn.conversationId] : []
    );
  }

  /** Running conversations missing from `knownIds` (the server's list), newest first. */
  provisionalConversations(
    knownIds: ReadonlySet<string>
  ): Array<{ id: string; title: string }> {
    return Array.from(this.turns.values())
      .flatMap(turn =>
        turn.conversationId && !knownIds.has(turn.conversationId)
          ? [{ id: turn.conversationId, title: turn.title }]
          : []
      )
      .reverse();
  }

  /** Whether this client saw the conversation's last turn end (its server `running` flag may lag). */
  finishedHere(conversationId: string): boolean {
    return this.finishedLocally.has(conversationId);
  }

  unseenFinishedIds(): string[] {
    return Array.from(this.unseenFinished);
  }

  /** Whether the conversation has an answer that finished in the background and was not opened since. */
  finishedUnopened(conversationId: string): boolean {
    return this.unopenedFinished.has(conversationId);
  }

  /** Clears the badge's notice for one conversation (its answer is still unopened). */
  markSeen(conversationId: string): void {
    if (this.unseenFinished.delete(conversationId)) this.notify(null);
  }

  /** The conversation was opened: its answer is neither unseen nor unopened any more. */
  markOpened(conversationId: string): void {
    const unseen = this.unseenFinished.delete(conversationId);
    const unopened = this.unopenedFinished.delete(conversationId);
    if (unseen || unopened) this.notify(null);
  }

  /**
   * The conversation was deleted: its running turn (if any) stops being
   * read and is dropped, and nothing of it is listed, marked or reported
   * again — otherwise a later update of that turn would bring the deleted
   * chat back into the history.
   */
  forgetConversation(conversationId: string): void {
    for (const [key, turn] of this.turns)
      if (turn.conversationId === conversationId) {
        turn.abort();
        this.turns.delete(key);
      }
    this.unseenFinished.delete(conversationId);
    this.unopenedFinished.delete(conversationId);
    this.finishedLocally.delete(conversationId);
    this.notify(null);
  }

  markAllSeen(): void {
    if (this.unseenFinished.size === 0) return;
    this.unseenFinished.clear();
    this.notify(null);
  }

  requestStop(key: string): void {
    const turn = this.turns.get(key);
    if (!turn || turn.stopRequested) return;
    turn.stopRequested = true;
    this.notify(turn);
  }

  /** The selected user or organisation changed: stops reading every turn and forgets all of it. */
  clear(): void {
    for (const turn of this.turns.values()) turn.abort();
    this.turns.clear();
    this.unseenFinished.clear();
    this.unopenedFinished.clear();
    this.finishedLocally.clear();
    this.notify(null);
  }

  /**
   * Called on every change, with the turn that changed (`null` for
   * registry-wide changes). Bound, like `getVersion`, so both can be handed
   * to `useSyncExternalStore` as they are.
   */
  subscribe = (
    listener: (turn: AgentBackgroundTurn | null) => void
  ): (() => void) => {
    const token = Symbol("listener");
    this.listeners.set(token, listener);
    return () => {
      this.listeners.delete(token);
    };
  };

  /** Changes with every notification — the snapshot for `useSyncExternalStore`. */
  getVersion = (): number => this.version;

  onFinished(listener: (notice: AgentFinishedTurnNotice) => void): () => void {
    const token = Symbol("finished-listener");
    this.finishedListeners.set(token, listener);
    return () => {
      this.finishedListeners.delete(token);
    };
  }

  private runningEntry(conversationId: string): Entry | null {
    for (const turn of this.turns.values())
      if (turn.conversationId === conversationId) return turn;
    return null;
  }

  private notify(turn: AgentBackgroundTurn | null): void {
    this.version += 1;
    this.listeners.forEach(listener => listener(turn));
  }
}
